import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Lock, Eye, EyeOff, ArrowRight, Plus, User, Phone, 
  Calendar, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Save, Trash2, Edit3,
  CheckCircle, Clock, XCircle, MessageSquare, Download, FileText, FileSpreadsheet,
  Bell, Filter, CalendarDays, X, Search, SortAsc, SortDesc, StickyNote,
  Play, Pause, RotateCcw, Timer, BarChart3, TrendingUp, Users, Target,
  PhoneMissed, CalendarPlus, Loader2, Link, Copy, Star, Video, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CORRECT_PASSWORD = '2026@Andrei';
const STORAGE_KEY = 'magnetic_transformation_access';

// Helper function to format date as "DD Luna YYYY"
const formatRomanianDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
                  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper function to export prospects to Excel (CSV)
const exportToExcel = (prospects) => {
  const statusLabels = {
    'new': 'Nou',
    'call1_done': 'Apel 1 ✓',
    'materials_sent': 'Materiale trimise',
    'call2_done': 'Apel 2 ✓',
    'zoom_scheduled': 'Consultanță programată',
    'converted': 'Convertit',
    'lost': 'Pierdut'
  };

  const headers = [
    'Nume', 'Telefon', 'Status', 'Ocupație', 'Oraș', 'Vârstă', 
    'Scor Împlinire', 'Obiectiv Prioritar', 'Venit Dorit', 'Venit Actual',
    'Callback Programat', 'Zoom Data/Ora', 'Note', 'Data Creare'
  ];

  const rows = prospects.map(p => [
    p.name || '',
    p.phone || '',
    statusLabels[p.status] || p.status || '',
    p.occupation || '',
    p.city || '',
    p.age || '',
    p.fulfillment_score || '',
    p.priority_goal || '',
    p.target_income || '',
    p.current_income || '',
    p.callback_time || '',
    p.zoom_datetime || '',
    (p.notes || '').replace(/\n/g, ' '),
    formatRomanianDate(p.created_at)
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `prospecti_magnetic_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// Helper function to export single prospect to PDF
const exportProspectToPDF = (prospect) => {
  const statusLabels = {
    'new': 'Nou',
    'call1_done': 'Apel 1 ✓',
    'materials_sent': 'Materiale trimise',
    'call2_done': 'Apel 2 ✓',
    'zoom_scheduled': 'Consultanță programată',
    'converted': 'Convertit',
    'lost': 'Pierdut'
  };

  const priorityLabels = {
    'financial': 'Financiar',
    'relationships': 'Relații',
    'health': 'Sănătate',
    'career': 'Carieră'
  };

  const content = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Fișă Prospect - ${prospect.name}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #6b21a8; border-bottom: 2px solid #6b21a8; padding-bottom: 10px; }
        h2 { color: #4c1d95; margin-top: 30px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 20px 0; }
        .info-item { background: #f3f4f6; padding: 12px; border-radius: 8px; }
        .info-label { font-size: 12px; color: #6b7280; margin-bottom: 4px; }
        .info-value { font-weight: bold; color: #1f2937; }
        .section { margin: 25px 0; padding: 20px; background: #faf5ff; border-radius: 12px; border-left: 4px solid #6b21a8; }
        .notes { white-space: pre-wrap; background: #f9fafb; padding: 15px; border-radius: 8px; }
        .status { display: inline-block; padding: 6px 12px; border-radius: 20px; background: #6b21a8; color: white; font-weight: bold; }
        .header { display: flex; justify-content: space-between; align-items: center; }
        .date { color: #6b7280; font-size: 14px; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🧲 Fișă Prospect</h1>
        <span class="date">Exportat: ${formatRomanianDate(new Date().toISOString())}</span>
      </div>
      
      <div class="section">
        <h2 style="margin-top:0">📋 Informații de bază</h2>
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">Nume</div>
            <div class="info-value">${prospect.name || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Telefon</div>
            <div class="info-value">${prospect.phone || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Status</div>
            <div class="info-value"><span class="status">${statusLabels[prospect.status] || prospect.status}</span></div>
          </div>
          <div class="info-item">
            <div class="info-label">Data creare</div>
            <div class="info-value">${formatRomanianDate(prospect.created_at)}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <h2 style="margin-top:0">📞 Apelul 1 - Calificare</h2>
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">A vizionat webinarul?</div>
            <div class="info-value">${prospect.watched_webinar === 'yes' ? 'Da' : prospect.watched_webinar === 'no' ? 'Nu' : prospect.watched_webinar === 'partial' ? 'Parțial' : '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Ocupație</div>
            <div class="info-value">${prospect.occupation || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Vârstă</div>
            <div class="info-value">${prospect.age || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Oraș</div>
            <div class="info-value">${prospect.city || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Scor împlinire (1-10)</div>
            <div class="info-value">${prospect.fulfillment_score || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Obiectiv prioritar</div>
            <div class="info-value">${priorityLabels[prospect.priority_goal] || prospect.priority_goal || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Venit dorit</div>
            <div class="info-value">${prospect.target_income || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Venit actual</div>
            <div class="info-value">${prospect.current_income || '-'}</div>
          </div>
        </div>
        
        ${prospect.webinar_feedback ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Feedback webinar</div><div class="notes">${prospect.webinar_feedback}</div></div>` : ''}
        ${prospect.knows_law_of_attraction ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Cunoștințe legea atracției</div><div class="notes">${prospect.knows_law_of_attraction}</div></div>` : ''}
        ${prospect.whats_missing ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Ce lipsește pentru 9-10</div><div class="notes">${prospect.whats_missing}</div></div>` : ''}
        ${prospect.blockers ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Blocaje</div><div class="notes">${prospect.blockers}</div></div>` : ''}
        ${prospect.callback_time ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Callback programat</div><div class="info-value">${prospect.callback_time}</div></div>` : ''}
      </div>

      <div class="section">
        <h2 style="margin-top:0">📞 Apelul 2 - Follow-up</h2>
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">A vizionat materialele?</div>
            <div class="info-value">${prospect.call2_watched_materials === 'yes' ? 'Da' : prospect.call2_watched_materials === 'no' ? 'Nu' : prospect.call2_watched_materials === 'partial' ? 'Parțial' : '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Deschis să investească?</div>
            <div class="info-value">${prospect.call2_open_to_invest === 'yes' ? 'Da' : prospect.call2_open_to_invest === 'no' ? 'Nu' : prospect.call2_open_to_invest === 'maybe' ? 'Poate' : '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Zoom programat?</div>
            <div class="info-value">${prospect.zoom_scheduled === 'yes' ? 'Da' : 'Nu'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Data/Ora Zoom</div>
            <div class="info-value">${prospect.zoom_datetime || '-'}</div>
          </div>
        </div>
        ${prospect.call2_feedback ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Feedback materiale</div><div class="notes">${prospect.call2_feedback}</div></div>` : ''}
        ${prospect.call2_questions ? `<div class="info-item" style="margin-top:15px"><div class="info-label">Întrebări</div><div class="notes">${prospect.call2_questions}</div></div>` : ''}
      </div>

      ${prospect.notes ? `
      <div class="section">
        <h2 style="margin-top:0">📝 Note adiționale</h2>
        <div class="notes">${prospect.notes}</div>
      </div>
      ` : ''}

      <div style="margin-top:40px; text-align:center; color:#6b7280; font-size:12px;">
        Transformare Magnetică - Fișă generată automat
      </div>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(content);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
};

const MagneticTransformation = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  
  // Motivational quotes
  const motivationalQuotes = [
    "Succesul este suma micilor eforturi repetate zi de zi.",
    "Fiecare NU te apropie de următorul DA.",
    "Nu vinde produse, vinde transformări.",
    "Energia ta atrage rezultatele tale.",
    "Azi e ziua în care faci diferența.",
    "Fiecare apel e o oportunitate de a schimba o viață.",
    "Crede în proces, rezultatele vor veni.",
    "Tu ești soluția pe care ei o caută.",
    "Persistența bate talentul.",
    "Fă azi ce alții nu vor, ca să ai mâine ce alții nu au.",
    "Succesul lasă urme. Urmează-le.",
    "Atitudinea ta determină altitudinea ta.",
    "Fiecare conversație e un pas spre abundență.",
    "Magnetismul tău atrage oamenii potriviți.",
    "Azi plantezi, mâine recoltezi."
  ];
  
  const [currentQuote] = useState(() => 
    motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]
  );
  
  // Prospects state
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [newProspectName, setNewProspectName] = useState('');
  const [newProspectPhone, setNewProspectPhone] = useState('');
  const [showNewProspectForm, setShowNewProspectForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [upcomingCall, setUpcomingCall] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quickNoteProspect, setQuickNoteProspect] = useState(null);
  const [quickNoteText, setQuickNoteText] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProspects, setTotalProspects] = useState(0);
  const [stats, setStats] = useState({ total: 0, by_status: {}, total_call_duration: 0 });
  const [dateCounts, setDateCounts] = useState({ today: 0, yesterday: 0, last7: 0 });
  const PROSPECTS_PER_PAGE = 50;
  
  // Call timer state
  const [callTimer, setCallTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerIntervalRef = useRef(null);

  // Load timer when prospect changes
  useEffect(() => {
    if (selectedProspect) {
      pauseTimer();
      setCallTimer(selectedProspect.call_duration || 0);
    }
  }, [selectedProspect?.id]);

  // Check if already authenticated
  useEffect(() => {
    const storedAuth = localStorage.getItem(STORAGE_KEY);
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
      loadProspects();
    }
    setInitialLoading(false);
  }, []);

  // Check for upcoming calls every minute
  useEffect(() => {
    const checkUpcomingCalls = () => {
      const now = new Date();
      const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60000);
      
      // Find prospects with callback_time or zoom_datetime in the next 30 minutes
      const upcoming = prospects.find(p => {
        if (p.callback_time) {
          // Parse callback_time (e.g., "Joi 14:00" or "14:00")
          const timeMatch = p.callback_time.match(/(\d{1,2}):(\d{2})/);
          if (timeMatch) {
            const callTime = new Date();
            callTime.setHours(parseInt(timeMatch[1]), parseInt(timeMatch[2]), 0);
            if (callTime > now && callTime <= thirtyMinutesFromNow) {
              return true;
            }
          }
        }
        if (p.zoom_datetime) {
          const timeMatch = p.zoom_datetime.match(/(\d{1,2}):(\d{2})/);
          if (timeMatch) {
            const zoomTime = new Date();
            zoomTime.setHours(parseInt(timeMatch[1]), parseInt(timeMatch[2]), 0);
            if (zoomTime > now && zoomTime <= thirtyMinutesFromNow) {
              return true;
            }
          }
        }
        return false;
      });
      
      setUpcomingCall(upcoming || null);
    };

    checkUpcomingCalls();
    const interval = setInterval(checkUpcomingCalls, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [prospects]);

  // Get scheduled calls for calendar (from currently loaded prospects)
  const scheduledCalls = prospects.filter(p => p.callback_time || p.zoom_datetime);

  // Timer functions
  const startTimer = () => {
    if (!isTimerRunning) {
      setIsTimerRunning(true);
      timerIntervalRef.current = setInterval(() => {
        setCallTimer(prev => prev + 1);
      }, 1000);
    }
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const resetTimer = () => {
    pauseTimer();
    setCallTimer(0);
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on duration (warning after 8 min, danger after 15 min)
  const getTimerColor = () => {
    if (callTimer > 900) return 'text-red-400 bg-red-500/20 border-red-400'; // > 15 min
    if (callTimer > 480) return 'text-yellow-400 bg-yellow-500/20 border-yellow-400'; // > 8 min
    return 'text-green-400 bg-green-500/20 border-green-400';
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const loadProspects = async (page = 1, status = statusFilter, search = searchQuery, dateF = dateFilter) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        password: CORRECT_PASSWORD,
        page: page.toString(),
        limit: PROSPECTS_PER_PAGE.toString()
      });
      
      if (status && status !== 'all') params.append('status', status);
      if (search && search.trim()) params.append('search', search.trim());
      if (dateF && dateF !== 'all') params.append('date_filter', dateF);
      
      const response = await axios.get(`${API}/magnetic/prospects?${params.toString()}`);
      
      setProspects(response.data.prospects);
      setCurrentPage(response.data.pagination.page);
      setTotalPages(response.data.pagination.total_pages);
      setTotalProspects(response.data.pagination.total);
      setStats(response.data.stats);
      setDateCounts(response.data.date_counts);
    } catch (error) {
      console.error('Failed to load prospects:', error);
      toast.error('Eroare la încărcarea prospecților');
    } finally {
      setLoading(false);
    }
  };

  // Reload when filters change
  useEffect(() => {
    if (isAuthenticated) {
      const debounceTimer = setTimeout(() => {
        loadProspects(1, statusFilter, searchQuery, dateFilter);
      }, searchQuery ? 300 : 0); // Debounce only for search
      
      return () => clearTimeout(debounceTimer);
    }
  }, [statusFilter, searchQuery, dateFilter, isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    
    if (password === CORRECT_PASSWORD) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsAuthenticated(true);
      loadProspects();
    } else {
      setError('Parolă incorectă. Încearcă din nou.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setPassword('');
    setSelectedProspect(null);
  };

  const createProspect = async () => {
    if (!newProspectName.trim()) {
      toast.error('Introdu numele prospectului');
      return;
    }
    
    // Duplicate check by phone number
    if (newProspectPhone.trim()) {
      const duplicate = prospects.find(p => p.phone === newProspectPhone.trim());
      if (duplicate) {
        toast.error(`⚠️ Există deja un prospect cu acest număr: ${duplicate.name}`);
        return;
      }
    }
    
    // Duplicate check by name (warning only)
    const nameDuplicate = prospects.find(p => p.name.toLowerCase() === newProspectName.trim().toLowerCase());
    if (nameDuplicate) {
      if (!confirm(`Există deja un prospect cu numele "${nameDuplicate.name}". Sigur vrei să continui?`)) {
        return;
      }
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/magnetic/prospects?password=${CORRECT_PASSWORD}`, {
        name: newProspectName.trim(),
        phone: newProspectPhone.trim() || null
      });
      setProspects([response.data, ...prospects]);
      setSelectedProspect(response.data);
      setNewProspectName('');
      setNewProspectPhone('');
      setShowNewProspectForm(false);
      toast.success('Prospect adăugat!');
    } catch (error) {
      console.error('Failed to create prospect:', error);
      toast.error('Eroare la adăugarea prospectului');
    } finally {
      setSaving(false);
    }
  };

  const updateProspect = async (updates) => {
    if (!selectedProspect) return;
    
    // Include current timer value in the update
    const updatesWithTimer = {
      ...updates,
      call_duration: callTimer
    };
    
    try {
      setSaving(true);
      const response = await axios.put(
        `${API}/magnetic/prospects/${selectedProspect.id}?password=${CORRECT_PASSWORD}`,
        updatesWithTimer
      );
      
      // Update local state
      setSelectedProspect(response.data);
      setProspects(prospects.map(p => p.id === response.data.id ? response.data : p));
      toast.success('Salvat!');
    } catch (error) {
      console.error('Failed to update prospect:', error);
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const deleteProspect = async (id) => {
    if (!confirm('Sigur vrei să ștergi acest prospect?')) return;
    
    try {
      await axios.delete(`${API}/magnetic/prospects/${id}?password=${CORRECT_PASSWORD}`);
      setProspects(prospects.filter(p => p.id !== id));
      if (selectedProspect?.id === id) {
        setSelectedProspect(null);
      }
      toast.success('Prospect șters');
    } catch (error) {
      console.error('Failed to delete prospect:', error);
      toast.error('Eroare la ștergere');
    }
  };

  // Quick action: Mark as "No Answer"
  const markNoAnswer = async (prospect) => {
    const timestamp = new Date().toLocaleString('ro-RO', { 
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
    });
    const existingNotes = prospect.notes || '';
    const newNote = `[${timestamp}] ❌ Nu a răspuns`;
    const updatedNotes = existingNotes ? `${existingNotes}\n${newNote}` : newNote;
    
    try {
      const response = await axios.put(
        `${API}/magnetic/prospects/${prospect.id}?password=${CORRECT_PASSWORD}`,
        { ...prospect, notes: updatedNotes }
      );
      setProspects(prospects.map(p => p.id === response.data.id ? response.data : p));
      if (selectedProspect?.id === prospect.id) {
        setSelectedProspect(response.data);
      }
      toast.info('Marcat: Nu a răspuns');
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Eroare');
    }
  };

  // Quick action: Reschedule +2 days
  const reschedule2Days = async (prospect) => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 2);
    const days = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
    const dayName = days[futureDate.getDay()];
    const currentHour = new Date().getHours();
    const callbackTime = `${dayName} ${currentHour}:00`;
    
    const timestamp = new Date().toLocaleString('ro-RO', { 
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
    });
    const existingNotes = prospect.notes || '';
    const newNote = `[${timestamp}] 🔄 Reprogramat pentru ${callbackTime}`;
    const updatedNotes = existingNotes ? `${existingNotes}\n${newNote}` : newNote;
    
    try {
      const response = await axios.put(
        `${API}/magnetic/prospects/${prospect.id}?password=${CORRECT_PASSWORD}`,
        { ...prospect, callback_time: callbackTime, notes: updatedNotes }
      );
      setProspects(prospects.map(p => p.id === response.data.id ? response.data : p));
      if (selectedProspect?.id === prospect.id) {
        setSelectedProspect(response.data);
      }
      toast.success(`Reprogramat: ${callbackTime}`);
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Eroare');
    }
  };

  const saveQuickNote = async () => {
    if (!quickNoteProspect || !quickNoteText.trim()) return;
    
    try {
      setSaving(true);
      const existingNotes = quickNoteProspect.notes || '';
      const timestamp = new Date().toLocaleString('ro-RO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
      const newNotes = existingNotes 
        ? `${existingNotes}\n\n[${timestamp}] ${quickNoteText.trim()}`
        : `[${timestamp}] ${quickNoteText.trim()}`;
      
      const response = await axios.put(
        `${API}/magnetic/prospects/${quickNoteProspect.id}?password=${CORRECT_PASSWORD}`,
        { ...quickNoteProspect, notes: newNotes }
      );
      
      setProspects(prospects.map(p => p.id === response.data.id ? response.data : p));
      if (selectedProspect?.id === quickNoteProspect.id) {
        setSelectedProspect(response.data);
      }
      setQuickNoteProspect(null);
      setQuickNoteText('');
      toast.success('Notă adăugată!');
    } catch (error) {
      console.error('Failed to save quick note:', error);
      toast.error('Eroare la salvarea notiței');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { label: 'Nou', color: 'bg-blue-500' },
      'call1_done': { label: 'Apel 1 ✓', color: 'bg-yellow-500' },
      'materials_sent': { label: 'Materiale trimise', color: 'bg-orange-500' },
      'call2_done': { label: 'Apel 2 ✓', color: 'bg-purple-500' },
      'zoom_scheduled': { label: 'Consultanță', color: 'bg-indigo-500' },
      'converted': { label: 'Convertit', color: 'bg-green-500' },
      'lost': { label: 'Pierdut', color: 'bg-red-500' }
    };
    const config = statusConfig[status] || statusConfig['new'];
    return <Badge className={`${config.color} text-white text-xs`}>{config.label}</Badge>;
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Password Gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <Card className="relative z-10 w-full max-w-md p-8 bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Zonă Privată</h1>
            <p className="text-white/70 text-sm">Transformare Magnetică - Call Tracker</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Introdu parola"
                className="bg-white/10 border-white/30 text-white placeholder:text-white/50 pr-12"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <Button type="submit" className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold py-3">
              Intră <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 relative">
      <Toaster position="top-right" richColors />
      
      {/* Background - fixed to cover entire viewport */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 -z-10"></div>
      
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Quick Note Modal */}
      {quickNoteProspect && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-white/20 rounded-xl p-4 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold flex items-center gap-2">
                <StickyNote className="h-5 w-5 text-yellow-400" />
                Notă rapidă pentru {quickNoteProspect.name}
              </h3>
              <button onClick={() => setQuickNoteProspect(null)} className="text-white/50 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <textarea
              value={quickNoteText}
              onChange={(e) => setQuickNoteText(e.target.value)}
              placeholder="Scrie nota aici..."
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder:text-white/40 min-h-[100px] resize-none mb-3"
              autoFocus
            />
            <div className="flex gap-2">
              <Button 
                onClick={() => setQuickNoteProspect(null)} 
                variant="outline" 
                className="flex-1 border-white/30 text-white hover:bg-white/10"
              >
                Anulează
              </Button>
              <Button 
                onClick={saveQuickNote} 
                disabled={saving || !quickNoteText.trim()}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {saving ? 'Se salvează...' : 'Salvează Nota'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="text-white font-bold text-lg">🧲 Transformare Magnetică</div>
            <div className="hidden md:block text-white/60 italic text-sm font-light border-l border-white/20 pl-4">
              "{currentQuote}"
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Upcoming Call Notification */}
            {upcomingCall && (
              <div className="flex items-center gap-2 bg-orange-500/20 border border-orange-400 rounded-lg px-3 py-2 animate-pulse">
                <Bell className="h-4 w-4 text-orange-400" />
                <span className="text-orange-300 text-sm">
                  În 30 min: <strong>{upcomingCall.name}</strong> - {upcomingCall.phone || 'fără nr.'}
                </span>
              </div>
            )}
            {/* Dashboard Toggle */}
            <Button 
              onClick={() => setShowDashboard(!showDashboard)} 
              variant="outline" 
              size="sm" 
              className={`border-white/30 text-white hover:bg-white/10 ${showDashboard ? 'bg-green-500/30' : ''}`}
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            {/* Calendar Toggle */}
            <Button 
              onClick={() => setShowCalendar(!showCalendar)} 
              variant="outline" 
              size="sm" 
              className={`border-white/30 text-white hover:bg-white/10 ${showCalendar ? 'bg-purple-500/30' : ''}`}
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
              Ieși
            </Button>
          </div>
        </div>
      </header>

      {/* Dashboard Sidebar */}
      {showDashboard && (
        <div className="fixed right-0 top-16 w-96 h-[calc(100vh-4rem)] bg-slate-900/95 backdrop-blur-xl border-l border-white/10 z-40 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-green-400" />
                Dashboard
              </h3>
              <button onClick={() => setShowDashboard(false)} className="text-white/50 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Funnel Stats */}
            <div className="space-y-3">
              {/* Total */}
              <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" /> Total Prospecți
                  </span>
                  <span className="text-2xl font-bold text-white">{stats.total}</span>
                </div>
              </div>

              {/* Pipeline Funnel */}
              <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-400" /> Pipeline
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-400 text-sm">🆕 Noi</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.new || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.new || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-yellow-400 text-sm">📞 Apel 1 ✓</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.call1_done || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.call1_done || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-orange-400 text-sm">📧 Materiale</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.materials_sent || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.materials_sent || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-purple-400 text-sm">📞 Apel 2 ✓</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.call2_done || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.call2_done || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-400 text-sm">📅 Consultanță</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-cyan-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.zoom_scheduled || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.zoom_scheduled || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 text-sm">✅ Convertiți</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.converted || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.converted || 0}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-red-400 text-sm">❌ Pierduți</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-red-400 rounded-full"
                          style={{ width: `${stats.total ? ((stats.by_status?.lost || 0) / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-white font-bold text-sm w-8">{stats.by_status?.lost || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Conversion Rate */}
              <div className="p-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg border border-green-400/30">
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-sm flex items-center gap-2">
                    <Target className="h-4 w-4 text-green-400" /> Rată Conversie
                  </span>
                  <span className="text-2xl font-bold text-green-400">
                    {stats.total > 0 
                      ? Math.round(((stats.by_status?.converted || 0) / stats.total) * 100) 
                      : 0}%
                  </span>
                </div>
                <p className="text-white/50 text-xs mt-1">
                  {stats.by_status?.converted || 0} din {stats.total} prospecți
                </p>
              </div>

              {/* Active Pipeline */}
              <div className="p-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg border border-blue-400/30">
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-400" /> În Lucru Activ
                  </span>
                  <span className="text-2xl font-bold text-blue-400">
                    {(stats.by_status?.new || 0) + (stats.by_status?.call1_done || 0) + (stats.by_status?.materials_sent || 0) + (stats.by_status?.call2_done || 0) + (stats.by_status?.zoom_scheduled || 0)}
                  </span>
                </div>
                <p className="text-white/50 text-xs mt-1">
                  Prospecți activi în pipeline
                </p>
              </div>

              {/* Total Call Time */}
              <div className="p-3 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 rounded-lg border border-orange-400/30">
                <div className="flex items-center justify-between">
                  <span className="text-white/70 text-sm flex items-center gap-2">
                    <Timer className="h-4 w-4 text-orange-400" /> Timp Total Call-uri
                  </span>
                  <span className="text-2xl font-bold text-orange-400">
                    {(() => {
                      const totalSeconds = stats.total_call_duration || 0;
                      const hours = Math.floor(totalSeconds / 3600);
                      const mins = Math.floor((totalSeconds % 3600) / 60);
                      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
                    })()}
                  </span>
                </div>
                <p className="text-white/50 text-xs mt-1">
                  Media: {stats.total > 0 
                    ? Math.round((stats.total_call_duration || 0) / stats.total / 60) 
                    : 0} min/prospect
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mini Calendar Sidebar */}
      {showCalendar && (
        <div className="fixed right-0 top-16 w-80 h-[calc(100vh-4rem)] bg-slate-900/95 backdrop-blur-xl border-l border-white/10 z-40 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-purple-400" />
                Programări
              </h3>
              <button onClick={() => setShowCalendar(false)} className="text-white/50 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {scheduledCalls.length === 0 ? (
              <p className="text-white/50 text-sm text-center py-8">Nicio programare încă</p>
            ) : (
              <div className="space-y-3">
                {scheduledCalls.map(p => (
                  <div 
                    key={p.id} 
                    className="p-3 bg-white/5 rounded-lg border border-white/10 hover:border-purple-400/50 cursor-pointer transition-all"
                    onClick={() => {
                      setSelectedProspect(p);
                      setShowCalendar(false);
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-purple-400" />
                      <span className="text-white font-medium text-sm">{p.name}</span>
                    </div>
                    {p.phone && (
                      <div className="flex items-center gap-2 text-white/60 text-xs mb-2">
                        <Phone className="h-3 w-3" />
                        {p.phone}
                      </div>
                    )}
                    {p.callback_time && (
                      <div className="flex items-center gap-2 text-yellow-400 text-xs">
                        <Clock className="h-3 w-3" />
                        <span>Callback: {p.callback_time}</span>
                      </div>
                    )}
                    {p.zoom_datetime && (
                      <div className="flex items-center gap-2 text-green-400 text-xs mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>Zoom: {p.zoom_datetime}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-[1800px] mx-auto p-4 relative z-10 min-h-[calc(100vh-5rem)]">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          
          {/* Left Panel - Prospects List */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-white/10 backdrop-blur-xl border-white/20 lg:h-[calc(100vh-7rem)] flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-white">Prospecți</h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => exportToExcel(prospects)}
                    size="sm"
                    variant="outline"
                    className="border-green-400 text-green-400 hover:bg-green-500/20"
                    title="Export Excel"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => setShowNewProspectForm(!showNewProspectForm)}
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Search Box */}
              <div className="mb-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Caută după nume sau telefon..."
                  className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/40 text-sm"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Date Filter - Quick Buttons */}
              <div className="mb-3 flex gap-1 flex-wrap">
                <button
                  onClick={() => setDateFilter('all')}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    dateFilter === 'all' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Toate
                </button>
                <button
                  onClick={() => setDateFilter('today')}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    dateFilter === 'today' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Azi ({dateCounts.today})
                </button>
                <button
                  onClick={() => setDateFilter('yesterday')}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    dateFilter === 'yesterday' 
                      ? 'bg-yellow-500 text-black' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Ieri ({dateCounts.yesterday})
                </button>
                <button
                  onClick={() => setDateFilter('last7')}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    dateFilter === 'last7' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  7 zile ({dateCounts.last7})
                </button>
              </div>

              {/* Status Filter */}
              <div className="mb-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-xs"
                >
                  <option value="all" className="bg-slate-800">Toți ({stats.total})</option>
                  <option value="new" className="bg-slate-800">🆕 Noi ({stats.by_status?.new || 0})</option>
                  <option value="call1_done" className="bg-slate-800">📞 Apel 1 ✓ ({stats.by_status?.call1_done || 0})</option>
                  <option value="materials_sent" className="bg-slate-800">📧 Materiale ({stats.by_status?.materials_sent || 0})</option>
                  <option value="call2_done" className="bg-slate-800">📞 Apel 2 ✓ ({stats.by_status?.call2_done || 0})</option>
                  <option value="zoom_scheduled" className="bg-slate-800">📅 Consultanță ({stats.by_status?.zoom_scheduled || 0})</option>
                  <option value="converted" className="bg-slate-800">✅ Convertiți ({stats.by_status?.converted || 0})</option>
                  <option value="lost" className="bg-slate-800">❌ Pierduți ({stats.by_status?.lost || 0})</option>
                </select>
              </div>

              {/* New Prospect Form */}
              {showNewProspectForm && (
                <div className="mb-3 p-3 bg-white/5 rounded-lg space-y-2">
                  <Input
                    value={newProspectName}
                    onChange={(e) => setNewProspectName(e.target.value)}
                    placeholder="Nume prospect"
                    className="bg-white/10 border-white/30 text-white placeholder:text-white/50 text-sm"
                  />
                  <Input
                    value={newProspectPhone}
                    onChange={(e) => setNewProspectPhone(e.target.value)}
                    placeholder="Telefon (opțional)"
                    className="bg-white/10 border-white/30 text-white placeholder:text-white/50 text-sm"
                  />
                  <Button onClick={createProspect} disabled={saving} className="w-full bg-green-500 hover:bg-green-600 text-sm">
                    {saving ? 'Se salvează...' : 'Adaugă Prospect'}
                  </Button>
                </div>
              )}

              {/* Prospects List */}
              <div className="space-y-2 flex-1 overflow-y-auto relative">
                {/* Loading overlay - only on list */}
                {loading && (
                  <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                    <Loader2 className="h-6 w-6 text-purple-400 animate-spin" />
                  </div>
                )}
                
                {prospects.length === 0 && !loading ? (
                  <p className="text-white/50 text-sm text-center py-4">
                    {searchQuery ? 'Niciun rezultat pentru căutare' : (statusFilter === 'all' ? 'Niciun prospect încă' : 'Niciun prospect cu acest status')}
                  </p>
                ) : (
                  prospects.map((prospect) => (
                    <div
                      key={prospect.id}
                      onClick={() => setSelectedProspect(prospect)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedProspect?.id === prospect.id
                          ? 'bg-purple-500/30 border border-purple-400'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <User className="h-4 w-4 text-white/60 flex-shrink-0" />
                          <span className="text-white font-medium text-sm truncate">{prospect.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickNoteProspect(prospect);
                              setQuickNoteText('');
                            }}
                            className="p-1 text-yellow-400/60 hover:text-yellow-400 hover:bg-yellow-500/20 rounded transition-all"
                            title="Adaugă notă rapidă"
                          >
                            <StickyNote className="h-3 w-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteProspect(prospect.id);
                            }}
                            className="p-1 text-red-400/60 hover:text-red-400 hover:bg-red-500/20 rounded transition-all"
                            title="Șterge prospect"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                          {getStatusBadge(prospect.status)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        {prospect.phone && (
                          <div className="flex items-center gap-2 text-white/50 text-xs">
                            <Phone className="h-3 w-3" />
                            {prospect.phone}
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-white/40 text-xs ml-auto">
                          {prospect.call_duration > 0 && (
                            <div className="flex items-center gap-1">
                              <Timer className="h-3 w-3" />
                              {Math.floor(prospect.call_duration / 60)}:{(prospect.call_duration % 60).toString().padStart(2, '0')}
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {prospect.created_at ? (() => {
                              const d = new Date(prospect.created_at);
                              const months = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              return `${d.getDate()} ${months[d.getMonth()]}`;
                            })() : '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-3 flex items-center justify-between text-white/70 text-sm">
                  <span>{totalProspects} prospecți</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadProspects(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="p-1 hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span>{currentPage} / {totalPages}</span>
                    <button
                      onClick={() => loadProspects(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                      className="p-1 hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold text-white">{stats.total}</div>
                  <div className="text-white/50 text-xs">Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">
                    {stats.by_status?.converted || 0}
                  </div>
                  <div className="text-white/50 text-xs">Convertiți</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {(stats.by_status?.new || 0) + (stats.by_status?.call1_done || 0) + (stats.by_status?.materials_sent || 0) + (stats.by_status?.call2_done || 0) + (stats.by_status?.zoom_scheduled || 0)}
                  </div>
                  <div className="text-white/50 text-xs">În lucru</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Panel - Script & Form */}
          <div className="lg:col-span-4">
            {!selectedProspect ? (
              <Card className="p-8 bg-white/10 backdrop-blur-xl border-white/20 text-center lg:h-[calc(100vh-7rem)] flex flex-col items-center justify-center">
                <MessageSquare className="h-12 w-12 text-white/30 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Selectează un prospect</h3>
                <p className="text-white/60">Alege un prospect din lista din stânga sau adaugă unul nou pentru a începe</p>
              </Card>
            ) : (
              <ScriptForm 
                prospect={selectedProspect} 
                onUpdate={updateProspect}
                onDelete={() => deleteProspect(selectedProspect.id)}
                onExportPDF={() => exportProspectToPDF(selectedProspect)}
                saving={saving}
                timer={{
                  value: callTimer,
                  isRunning: isTimerRunning,
                  formatted: formatTimer(callTimer),
                  colorClass: getTimerColor(),
                  start: startTimer,
                  pause: pauseTimer,
                  reset: resetTimer
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Input Components - defined outside to prevent focus loss
const FormTextInput = ({ label, value, onChange, placeholder, multiline = false }) => (
  <div className="space-y-1 mb-5">
    {label && <label className="text-white/70 text-sm font-medium">{label}</label>}
    {multiline ? (
      <textarea
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder:text-white/40 text-[17px] min-h-[100px] resize-none"
      />
    ) : (
      <Input
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-white/10 border-white/20 text-white placeholder:text-white/40 text-[17px] h-11"
      />
    )}
  </div>
);

const FormSelectInput = ({ label, value, onChange, options }) => (
  <div className="space-y-1 mb-5">
    {label && <label className="text-white/70 text-sm font-medium">{label}</label>}
    <select
      value={value || ''}
      onChange={onChange}
      className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white text-[17px] h-11"
    >
      <option value="" className="bg-slate-800">Selectează...</option>
      {options.map(opt => (
        <option key={opt.value} value={opt.value} className="bg-slate-800">{opt.label}</option>
      ))}
    </select>
  </div>
);

const ScriptText = ({ children, variant = 'default' }) => {
  const borderColor = variant === 'call2' ? 'border-green-400' : 'border-blue-400';
  return (
    <p className={`text-white/90 text-[17px] leading-relaxed mt-6 mb-3 p-4 bg-white/5 rounded-lg border-l-4 ${borderColor}`}>
      {children}
    </p>
  );
};

// Testimonial Link Card Component with editable URL and copy functionality
const TestimonialLinkCard = ({ title, description, linkPlaceholder }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const storageKey = `testimonial_link_${title.replace(/[^a-zA-Z0-9]/g, '_')}`;
  
  // Load saved link from localStorage on mount
  useEffect(() => {
    const savedLink = localStorage.getItem(storageKey);
    if (savedLink) {
      setLinkUrl(savedLink);
    }
  }, [storageKey]);
  
  // Save link to localStorage when it changes
  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setLinkUrl(newLink);
    localStorage.setItem(storageKey, newLink);
  };
  
  const copyToClipboard = async () => {
    if (!linkUrl.trim()) {
      toast.error('Adaugă mai întâi un link!');
      return;
    }
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      toast.success('Link copiat!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Eroare la copiere');
    }
  };
  
  const openLink = () => {
    if (!linkUrl.trim()) {
      toast.error('Adaugă mai întâi un link!');
      return;
    }
    window.open(linkUrl, '_blank');
  };
  
  return (
    <div className="p-3 bg-white/5 rounded-lg border border-white/10 hover:border-pink-400/30 transition-all">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-white font-medium text-sm">{title}</h4>
          <p className="text-white/50 text-xs">{description}</p>
        </div>
        <div className="flex gap-1">
          <button
            onClick={openLink}
            className="p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all"
            title="Deschide link"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={copyToClipboard}
            className={`p-1.5 rounded transition-all ${
              copied 
                ? 'bg-green-500/30 text-green-400' 
                : 'bg-white/10 hover:bg-pink-500/30 text-white/70 hover:text-pink-400'
            }`}
            title="Copiază link"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <Input
        value={linkUrl}
        onChange={handleLinkChange}
        placeholder={linkPlaceholder}
        className="bg-white/10 border-white/20 text-white placeholder:text-white/30 text-sm h-9"
      />
    </div>
  );
};

// WhatsApp Message Card Component
const WhatsAppMessageCard = ({ title, message, icon = "📱" }) => {
  const [copied, setCopied] = useState(false);
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Mesaj copiat!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Eroare la copiere');
    }
  };
  
  return (
    <div className="p-3 bg-white/5 rounded-lg border border-white/10 hover:border-green-400/30 transition-all">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <h4 className="text-white font-medium text-sm">{title}</h4>
        </div>
        <button
          onClick={copyToClipboard}
          className={`px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all text-sm font-medium ${
            copied 
              ? 'bg-green-500/30 text-green-400' 
              : 'bg-green-500/20 hover:bg-green-500/30 text-green-400 hover:text-green-300'
          }`}
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? 'Copiat!' : 'Copiază'}
        </button>
      </div>
      <div className="text-white/70 text-xs whitespace-pre-wrap bg-black/20 p-3 rounded-lg max-h-40 overflow-y-auto">
        {message}
      </div>
    </div>
  );
};

// WhatsApp Messages Data
const WHATSAPP_MESSAGES = [
  {
    title: "Mesaj după primul apel",
    icon: "📞",
    message: `Bună,

Sunt Andrei Dubău, din partea lui Pop Răzvan Tiberiu.

Te-am sunat mai devreme pentru că te-ai înregistrat la webinar și vreau să îți trimit câteva materiale cu informații legate de programul Premium „Atragerea Abundenței" în viața ta – pe plan financiar, sănătate și relații – precum și înregistrarea webinarului.

📌 Explicarea programului – Manifestarea dorințelor:
https://vimeo.com/1023934916?share=copy#t=0

📌 Recenzii de la persoane care urmează programul de mentorat:
www.poprazvantiberiu.ro/testimoniale

📌 Webinar – informații utile (înregistrare):
https://event.webinarjam.com/vpoko/replay/3y818uy9c7puz666a6l15

Dacă nu sunt active linkurile, te rog să îmi salvezi numărul în agendă.

Când ai puțin timp, verifică te rog dacă se deschid linkurile și dă-mi un mesaj scurt.

Mulțumesc 🙏`
  }
];

// Script Form Component
const ScriptForm = ({ prospect, onUpdate, onDelete, onExportPDF, saving, timer }) => {
  const [formData, setFormData] = useState(prospect);
  const [expandedSections, setExpandedSections] = useState({
    call1: true,
    call2: false,
    notes: false,
    testimonials: false,
    whatsapp: false
  });

  useEffect(() => {
    setFormData(prospect);
  }, [prospect]);

  // Get first name from full name for personalization
  const getFirstName = () => {
    const name = formData.name || '';
    const firstName = name.split(' ')[0];
    return firstName || '[NUME]';
  };
  
  // Personalized name component - highlights the name in yellow
  const PersonalizedName = () => {
    const firstName = getFirstName();
    if (firstName === '[NUME]') {
      return <span className="text-yellow-400 font-semibold">[NUME]</span>;
    }
    return <span className="text-yellow-400 font-semibold">{firstName}</span>;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate(formData);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Calculate completion checklist
  const checklistItems = [
    { key: 'occupation', label: 'Ocupație', done: !!formData.occupation?.trim() },
    { key: 'satisfied_with_job', label: 'Mulțumit job', done: !!formData.satisfied_with_job },
    { key: 'knows_manifestation', label: 'Manifestare', done: !!formData.knows_manifestation },
    { key: 'priority_goal', label: 'Obiectiv', done: !!formData.priority_goal },
    { key: 'blockers', label: 'Blocaje', done: !!formData.blockers?.trim() },
    { key: 'callback_time', label: 'Callback', done: !!formData.callback_time?.trim() }
  ];
  const completedCount = checklistItems.filter(item => item.done).length;
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100);

  return (
    <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden lg:h-[calc(100vh-7rem)] flex flex-col">
      {/* Header */}
      <div className="p-4 bg-white/5 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold text-white">{formData.name}</h2>
            {formData.phone && <p className="text-white/60 text-sm">{formData.phone}</p>}
          </div>
          
          {/* Timer Section */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${timer.colorClass}`}>
            <Timer className="h-4 w-4" />
            <span className="font-mono text-lg font-bold">{timer.formatted}</span>
            <div className="flex items-center gap-1 ml-2">
              {!timer.isRunning ? (
                <button 
                  onClick={timer.start}
                  className="p-1.5 hover:bg-white/20 rounded transition-all"
                  title="Start"
                >
                  <Play className="h-4 w-4" />
                </button>
              ) : (
                <button 
                  onClick={timer.pause}
                  className="p-1.5 hover:bg-white/20 rounded transition-all"
                  title="Pauză"
                >
                  <Pause className="h-4 w-4" />
                </button>
              )}
              <button 
                onClick={timer.reset}
                className="p-1.5 hover:bg-white/20 rounded transition-all"
                title="Reset"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <FormSelectInput
              label=""
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              options={[
                { value: 'new', label: '🆕 Nou' },
                { value: 'call1_done', label: '📞 Apel 1 ✓' },
                { value: 'materials_sent', label: '📧 Materiale trimise' },
                { value: 'call2_done', label: '📞 Apel 2 ✓' },
                { value: 'zoom_scheduled', label: '📅 Consultanță programată' },
                { value: 'converted', label: '✅ Convertit' },
                { value: 'lost', label: '❌ Pierdut' }
              ]}
            />
            <Button onClick={onExportPDF} variant="outline" className="border-blue-400 text-blue-400 hover:bg-blue-500/20" title="Export PDF">
              <FileText className="h-4 w-4" />
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-green-500 hover:bg-green-600">
              <Save className="h-4 w-4 mr-1" /> {saving ? '...' : 'Salvează'}
            </Button>
            <Button onClick={onDelete} variant="outline" className="border-red-400 text-red-400 hover:bg-red-500/20">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Checklist vizual */}
        <div className="flex items-center gap-3 flex-wrap">
          {checklistItems.map((item) => (
            <div 
              key={item.key}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-all ${
                item.done 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-white/5 text-white/40'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${item.done ? 'bg-green-400' : 'bg-white/20'}`}></span>
              {item.label}
            </div>
          ))}
          <div className={`ml-auto text-sm font-bold ${
            completionPercent === 100 ? 'text-green-400' : 
            completionPercent >= 50 ? 'text-yellow-400' : 'text-white/50'
          }`}>
            {completionPercent}%
          </div>
        </div>
      </div>

      <div className="p-4 flex-1 overflow-y-auto space-y-4">
        
        {/* CALL 1 SECTION */}
        <div className="border-2 border-blue-400/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('call1')}
            className="w-full p-3 bg-blue-500/20 flex items-center justify-between text-white font-semibold text-lg"
          >
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-400 rounded-full"></span>
              📞 Apelul 1 - Calificare
            </span>
            {expandedSections.call1 ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          
          {expandedSections.call1 && (
            <div className="p-4 space-y-4 bg-blue-500/5 border-t border-blue-400/30">
              
              {/* INTRO - Deschidere */}
              <div className="bg-blue-500/10 -mx-4 px-4 py-3 border-b border-blue-400/20">
                <p className="text-blue-300 text-xs font-bold mb-2">🎯 DESCHIDERE</p>
                <ScriptText>
                  "Alo <PersonalizedName />? [EU] sunt, fac parte din echipa <strong>Transformare Magnetică</strong> cu <strong>Răzvan Tiberiu Pop</strong>. 
                  Te felicit că te-ai înscris la webinar! <strong>Ai reușit să-l vezi?</strong>"
                </ScriptText>
                <FormSelectInput
                  label="A vizionat webinarul?"
                  value={formData.watched_webinar}
                  onChange={(e) => handleChange('watched_webinar', e.target.value)}
                  options={[
                    { value: 'yes', label: 'Da' },
                    { value: 'no', label: 'Nu' }
                  ]}
                />
                {formData.watched_webinar === 'yes' && (
                  <ScriptText>"Super, <strong>mă bucur că ai reușit să intri!</strong>"</ScriptText>
                )}
                {formData.watched_webinar === 'no' && (
                  <ScriptText>"Nicio problemă, <strong>îți trimit înregistrarea</strong> la final să o poți vedea."</ScriptText>
                )}
              </div>

              {/* DISPONIBILITATE */}
              <ScriptText>
                "<strong>Ai 2 minute</strong> să-ți dau câteva detalii?"
              </ScriptText>
              <FormSelectInput
                label="Are disponibilitate?"
                value={formData.has_availability}
                onChange={(e) => handleChange('has_availability', e.target.value)}
                options={[
                  { value: 'yes', label: 'Da' },
                  { value: 'no', label: 'Nu - reprogramez' }
                ]}
              />
              {formData.has_availability === 'no' && (
                <div className="p-3 bg-yellow-500/20 border border-yellow-400/50 rounded-lg">
                  <p className="text-yellow-300 text-sm mb-2">⏰ Reprogramare</p>
                  <FormTextInput 
                    label="Când reprogramăm?" 
                    value={formData.reschedule_time}
                    onChange={(e) => handleChange('reschedule_time', e.target.value)}
                    placeholder="Ex: Mâine la 14:00" 
                  />
                </div>
              )}

              {formData.has_availability !== 'no' && (
                <>
                  {/* CUNOȘTINȚE MANIFESTARE */}
                  <div className="bg-purple-500/10 -mx-4 px-4 py-3 border-t border-purple-400/20">
                    <ScriptText>
                      "<strong>Ai ceva cunoștințe despre legea atracției sau manifestare?</strong>"
                    </ScriptText>
                    <FormSelectInput
                      label="Cunoștințe manifestare?"
                      value={formData.knows_manifestation}
                      onChange={(e) => handleChange('knows_manifestation', e.target.value)}
                      options={[
                        { value: 'yes', label: 'Da' },
                        { value: 'no', label: 'Nu' },
                        { value: 'partial', label: 'Puțin' }
                      ]}
                    />
                    <FormTextInput 
                      label="Detalii cunoștințe" 
                      value={formData.knows_law_of_attraction}
                      onChange={(e) => handleChange('knows_law_of_attraction', e.target.value)}
                      placeholder="Ce știe, ce a aplicat..." 
                    />
                  </div>

                  {/* DE CÂT TIMP ÎL ȘTIE PE RĂZVAN */}
                  <ScriptText>
                    "<strong>De cât timp îl știi pe Răzvan?</strong>"
                  </ScriptText>
                  <FormTextInput 
                    label="De cât timp îl știe pe Răzvan" 
                    value={formData.follows_razvan_since}
                    onChange={(e) => handleChange('follows_razvan_since', e.target.value)}
                    placeholder="Ex: De 6 luni, de pe YouTube..." 
                  />

                  {/* S1 - SITUAȚIA ACTUALĂ */}
                  <div className="bg-red-500/10 -mx-4 px-4 py-4 border-t border-red-400/20">
                    <p className="text-red-300 text-xs font-bold mb-3">📍 S1 - SITUAȚIA ACTUALĂ</p>
                    <ScriptText>
                      "Hai să vedem <strong>cum te putem ajuta</strong> și <strong>ce e prioritar să îmbunătățești</strong>."
                    </ScriptText>
                    
                    <ScriptText>"<strong>Cu ce te ocupi?</strong>"</ScriptText>
                    <FormTextInput 
                      label="Cu ce se ocupă" 
                      value={formData.occupation}
                      onChange={(e) => handleChange('occupation', e.target.value)}
                      placeholder="Ocupație..." 
                    />
                    
                    <ScriptText>"<strong>Ești mulțumit/ă de job?</strong>"</ScriptText>
                    <FormSelectInput
                      label="Mulțumit de job?"
                      value={formData.satisfied_with_job}
                      onChange={(e) => handleChange('satisfied_with_job', e.target.value)}
                      options={[
                        { value: 'yes', label: 'Da' },
                        { value: 'no', label: 'Nu' },
                        { value: 'partial', label: 'Parțial' }
                      ]}
                    />
                    <FormTextInput 
                      label="De ce? Ce nu merge?" 
                      value={formData.job_satisfaction_details}
                      onChange={(e) => handleChange('job_satisfaction_details', e.target.value)}
                      placeholder="Detalii..." 
                      multiline
                    />
                  </div>

                  {/* S2 - OBIECTIVE */}
                  <div className="bg-green-500/10 -mx-4 px-4 py-4 border-t border-green-400/20">
                    <p className="text-green-300 text-xs font-bold mb-3">🎯 S2 - OBIECTIVE</p>
                    <ScriptText>
                      "<strong>Imaginează-ți că ai un mentor</strong>. Ce obiective sunt prioritare pentru tine? 
                      <strong>Financiar, relații, sănătate, profesional?</strong>"
                    </ScriptText>
                    <FormSelectInput
                      label="Obiectiv prioritar"
                      value={formData.priority_goal}
                      onChange={(e) => handleChange('priority_goal', e.target.value)}
                      options={[
                        { value: 'financial', label: '💰 Financiar' },
                        { value: 'relationships', label: '❤️ Relații' },
                        { value: 'health', label: '🏃 Sănătate' },
                        { value: 'career', label: '💼 Profesional' }
                      ]}
                    />

                    {formData.priority_goal === 'financial' && (
                      <>
                        <ScriptText>
                          "<strong>Cât ți-ai dori să câștigi lunar</strong> ca să fii mulțumit?"
                        </ScriptText>
                        <FormTextInput 
                          label="Venit dorit (lunar)" 
                          value={formData.target_income}
                          onChange={(e) => handleChange('target_income', e.target.value)}
                          placeholder="Ex: 5000€/lună" 
                        />
                        <ScriptText>
                          "Ok, <strong>la ce sumă ești acum?</strong>"
                        </ScriptText>
                        <FormTextInput 
                          label="Venit actual (lunar)" 
                          value={formData.current_income}
                          onChange={(e) => handleChange('current_income', e.target.value)}
                          placeholder="Ex: 2000€/lună" 
                        />
                      </>
                    )}

                    {(formData.priority_goal === 'relationships' || formData.priority_goal === 'health' || formData.priority_goal === 'career') && (
                      <>
                        <ScriptText>"<strong>Spune-mi mai multe, ce îți dorești în zona asta?</strong>"</ScriptText>
                        <FormTextInput 
                          label="Obiectiv detaliat" 
                          value={formData.goal_details}
                          onChange={(e) => handleChange('goal_details', e.target.value)}
                          placeholder="Ce își dorește să obțină..." 
                          multiline
                        />
                      </>
                    )}
                  </div>

                  {/* BLOCAJE */}
                  <div className="bg-orange-500/10 -mx-4 px-4 py-4 border-t border-orange-400/20">
                    <p className="text-orange-300 text-xs font-bold mb-3">🚧 BLOCAJE</p>
                    <ScriptText>
                      "<strong>Ce crezi că te-a blocat până acum?</strong>"
                    </ScriptText>
                    <FormTextInput 
                      label="Blocaje identificate" 
                      value={formData.blockers}
                      onChange={(e) => handleChange('blockers', e.target.value)}
                      placeholder="Frici, temeri, gânduri limitative, lipsa unui plan..." 
                      multiline
                    />
                  </div>

                  {/* PREZENTARE PROGRAM */}
                  <div className="bg-purple-500/20 -mx-4 px-4 py-4 border-t border-purple-400/30">
                    <p className="text-purple-300 text-xs font-bold mb-3">💡 PREZENTARE PROGRAM</p>
                    <ScriptText>
                      "Perfect, hai să-ți explic pe scurt în ce constă programul.
                      <br/><br/>
                      E un program cu <strong>Răzvan ca mentor 1 la 1</strong>. Primești pe WhatsApp <strong>tehnici de manifestare</strong> pe care le faci practic, <strong>cu pixul și foaia</strong> - așa se formează conexiuni neuronale noi și se elimină blocajele.
                      <br/><br/>
                      După ce eliminăm blocajele, <strong>începi să atragi oamenii potriviți</strong> și circumstanțele optime pentru obiectivele tale.
                      <br/><br/>
                      Din partea ta e nevoie de <strong>30 min pe zi</strong>. Programul durează <strong>3-6 luni</strong>."
                    </ScriptText>
                    
                    <ScriptText>"<strong>Ai întrebări?</strong>"</ScriptText>
                    <FormTextInput 
                      label="Întrebări despre program" 
                      value={formData.program_questions}
                      onChange={(e) => handleChange('program_questions', e.target.value)}
                      placeholder="Ce întrebări a avut..." 
                      multiline
                    />
                  </div>

                  {/* ÎNCHEIERE APEL 1 */}
                  <div className="bg-cyan-500/10 -mx-4 px-4 py-4 border-t border-cyan-400/20">
                    <p className="text-cyan-300 text-xs font-bold mb-3">👋 ÎNCHEIERE APEL 1</p>
                    <ScriptText>
                      "Ok, <strong>îți las câteva materiale pe WhatsApp</strong> să le vizionezi și <strong>revin peste 2 zile cu apel</strong> să discutăm. 
                      <strong>La ce oră să revin cu apel?</strong>"
                    </ScriptText>
                    <FormTextInput 
                      label="Callback programat" 
                      value={formData.callback_time}
                      onChange={(e) => handleChange('callback_time', e.target.value)}
                      placeholder="Ex: Joi la 14:00" 
                    />
                    <ScriptText>
                      "Perfect, ne auzim la ora... <strong>Salvează-mi numărul în agendă!</strong>"
                    </ScriptText>
                    <FormSelectInput
                      label="A salvat numărul?"
                      value={formData.saved_phone_number}
                      onChange={(e) => handleChange('saved_phone_number', e.target.value)}
                      options={[
                        { value: 'yes', label: 'Da' },
                        { value: 'no', label: 'Nu' }
                      ]}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* CALL 2 SECTION */}
        <div className="border-2 border-green-400/50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('call2')}
            className="w-full p-3 bg-green-500/20 flex items-center justify-between text-white font-semibold text-lg"
          >
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-400 rounded-full"></span>
              📞 Apelul 2 - Follow-up & Programare Zoom
            </span>
            {expandedSections.call2 ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          
          {expandedSections.call2 && (
            <div className="p-4 space-y-4 bg-green-500/5 border-t border-green-400/30">
              
              {/* RECONECTARE */}
              <div className="bg-green-500/10 -mx-4 px-4 py-3 border-b border-green-400/20">
                <p className="text-green-300 text-xs font-bold mb-2">🔄 RECONECTARE</p>
                <ScriptText variant="call2">
                  "Alo <PersonalizedName />? Sunt [EU]... am revenit cu apel cum am stabilit... <strong>Ai vizionat materialele? Cum ți s-au părut?</strong>"
                </ScriptText>
                <FormSelectInput
                  label="A vizionat materialele?"
                  value={formData.call2_watched_materials}
                  onChange={(e) => handleChange('call2_watched_materials', e.target.value)}
                  options={[
                    { value: 'yes', label: 'Da' },
                    { value: 'no', label: 'Nu' }
                  ]}
                />
                {formData.call2_watched_materials === 'yes' && (
                  <FormTextInput 
                    label="Feedback materiale" 
                    value={formData.call2_feedback}
                    onChange={(e) => handleChange('call2_feedback', e.target.value)}
                    placeholder="Ce părere a avut..." 
                    multiline 
                  />
                )}
              </div>

              {/* ÎNTREBĂRI */}
              <ScriptText variant="call2">"<strong>Ai întrebări?</strong>"</ScriptText>
              <FormTextInput 
                label="Întrebări" 
                value={formData.call2_questions}
                onChange={(e) => handleChange('call2_questions', e.target.value)}
                placeholder="Ce întrebări a avut..." 
                multiline 
              />

              {/* PROGRAM PREMIUM */}
              <div className="bg-amber-500/10 -mx-4 px-4 py-4 border-t border-amber-400/20">
                <p className="text-amber-300 text-xs font-bold mb-3">💰 INVESTIȚIE</p>
                <ScriptText variant="call2">
                  "Doresc să specific că este un <strong>program de mentorat premium</strong> care are un cost. 
                  <strong>Ești dispus/ă să investești o sumă de bani în tine</strong> pentru a-ți atinge obiectivele?"
                </ScriptText>
                <FormSelectInput
                  label="Dispus/ă să investească?"
                  value={formData.call2_willing_to_invest}
                  onChange={(e) => handleChange('call2_willing_to_invest', e.target.value)}
                  options={[
                    { value: 'yes', label: 'Da' },
                    { value: 'no', label: 'Nu' },
                    { value: 'maybe', label: 'Depinde' }
                  ]}
                />
              </div>

              {/* PROGRAMARE ZOOM */}
              <div className="bg-blue-500/10 -mx-4 px-4 py-4 border-t border-blue-400/20">
                <p className="text-blue-300 text-xs font-bold mb-3">📅 PROGRAMARE ZOOM</p>
                <ScriptText variant="call2">
                  "Următorul pas e să stabilim o <strong>programare pe Zoom</strong> unde o să-ți explice un coleg în detaliu pașii din program.
                  <br/><br/>
                  În cadrul programării pe Zoom primești o <strong>ofertă de preț care este valabilă o singură dată</strong>. 
                  E nevoie să ai <strong>câteva sute de euro disponibili</strong> pentru a putea beneficia de această ofertă 
                  și să faci plata împreună cu colegul meu Expert, în cadrul întâlnirii."
                </ScriptText>
                
                <ScriptText variant="call2">
                  "<strong>Ai acești bani disponibili ca să poți profita de reducerea de preț?</strong>"
                </ScriptText>
                <FormSelectInput
                  label="Are avans disponibil?"
                  value={formData.call2_has_deposit}
                  onChange={(e) => handleChange('call2_has_deposit', e.target.value)}
                  options={[
                    { value: 'yes', label: 'Da' },
                    { value: 'no', label: 'Nu' }
                  ]}
                />
              </div>

              {/* PLAN DE PLĂȚI - dacă nu are avans */}
              {formData.call2_has_deposit === 'no' && (
                <div className="bg-yellow-500/10 -mx-4 px-4 py-4 border-t border-yellow-400/20">
                  <p className="text-yellow-300 text-xs font-bold mb-3">💳 OPȚIUNI DE PLATĂ</p>
                  <ScriptText variant="call2">
                    "Găsim soluții! Există varianta de a plăti printr-un <strong>plan de plăți</strong> stabilit împreună cu colegul meu.
                    <br/><br/>
                    Sau avem colaborare cu o <strong>instituție financiară (TBI)</strong> unde poți alege un număr mai mare de luni, 
                    între 12 și 60 luni maxim. Poți alege perioada și suma pe care să o achiți lunar - poate începe de la <strong>80€ lunar</strong>."
                  </ScriptText>
                  
                  <div className="mt-3 p-3 bg-slate-500/20 rounded-lg">
                    <p className="text-slate-300 text-xs font-bold mb-2">📋 Eligibilitate TBI:</p>
                    <ul className="text-white/80 text-sm space-y-1">
                      <li>• Angajat pe perioadă nedeterminată</li>
                      <li>• Minim 3 salarii la locul actual de muncă</li>
                      <li>• Fără istoric negativ la biroul de credite</li>
                      <li>• Valoarea ratelor prezente să nu depășească 40% din venit net</li>
                    </ul>
                  </div>
                  
                  <FormSelectInput
                    label="Este eligibil TBI?"
                    value={formData.call2_tbi_eligible}
                    onChange={(e) => handleChange('call2_tbi_eligible', e.target.value)}
                    options={[
                      { value: 'yes', label: 'Da, pare eligibil' },
                      { value: 'no', label: 'Nu' },
                      { value: 'unknown', label: 'Nu știu' }
                    ]}
                  />
                </div>
              )}

              {/* CALIFICARE & PROGRAMARE */}
              <div className="bg-green-500/20 -mx-4 px-4 py-4 border-t border-green-400/30">
                <p className="text-green-300 text-xs font-bold mb-3">✅ CALIFICARE & PROGRAMARE</p>
                <ScriptText variant="call2">
                  "Foarte bine <PersonalizedName />... din ce văd, <strong>te califici și ai potențial să faci lucruri mari</strong>.
                  <br/><br/>
                  <strong>Hai să facem o programare? În ce zi ești disponibil/ă și la ce oră?</strong>"
                </ScriptText>
                
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <FormTextInput 
                    label="Data Zoom" 
                    value={formData.consultation_date}
                    onChange={(e) => handleChange('consultation_date', e.target.value)}
                    placeholder="Ex: Luni 15 Feb" 
                  />
                  <FormTextInput 
                    label="Ora Zoom" 
                    value={formData.consultation_time}
                    onChange={(e) => handleChange('consultation_time', e.target.value)}
                    placeholder="Ex: 14:00" 
                  />
                </div>
              </div>

              {/* ÎNCHEIERE */}
              <div className="bg-purple-500/10 -mx-4 px-4 py-4 border-t border-purple-400/20">
                <p className="text-purple-300 text-xs font-bold mb-3">👋 ÎNCHEIERE APEL 2</p>
                <ScriptText variant="call2">
                  "Bun, rămâne atunci în ziua de <strong>[DATA]</strong> la ora <strong>[ORA]</strong>. 
                  <br/><br/>
                  O să primești toate detaliile și <strong>link-ul de programare pentru Zoom</strong> de la expertul nostru.
                  <br/><br/>
                  <strong>Pa, zi frumoasă!</strong>"
                </ScriptText>
              </div>
            </div>
          )}
        </div>

        {/* NOTES SECTION */}
        <div className="border border-amber-500/30 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('notes')}
            className="w-full p-3 bg-amber-500/10 flex items-center justify-between text-white font-semibold text-lg"
          >
            <span>📝 Note adiționale</span>
            {expandedSections.notes ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          
          {expandedSections.notes && (
            <div className="p-4 bg-amber-500/5">
              <FormTextInput 
                label="Note și observații" 
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Orice alte informații relevante despre acest prospect..." 
                multiline 
              />
            </div>
          )}
        </div>

        {/* WHATSAPP MESSAGES SECTION */}
        <div className="border border-green-500/30 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('whatsapp')}
            className="w-full p-3 bg-green-500/10 flex items-center justify-between text-white font-semibold text-lg"
          >
            <span>💬 Mesaje WhatsApp</span>
            {expandedSections.whatsapp ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          
          {expandedSections.whatsapp && (
            <div className="p-4 bg-green-500/5 space-y-4">
              <p className="text-white/70 text-sm mb-3">
                Mesaje predefinite pentru WhatsApp. Click pe "Copiază" și lipește în conversație.
              </p>
              
              {WHATSAPP_MESSAGES.map((msg, index) => (
                <WhatsAppMessageCard
                  key={index}
                  title={msg.title}
                  message={msg.message}
                  icon={msg.icon}
                />
              ))}
            </div>
          )}
        </div>

        {/* TESTIMONIALE SECTION */}
        <div className="border border-pink-500/30 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('testimonials')}
            className="w-full p-3 bg-pink-500/10 flex items-center justify-between text-white font-semibold text-lg"
          >
            <span>⭐ Testimoniale & Link-uri</span>
            {expandedSections.testimonials ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          
          {expandedSections.testimonials && (
            <div className="p-4 bg-pink-500/5 space-y-4">
              <p className="text-white/70 text-sm mb-3">
                Link-uri utile pentru a trimite prospectului. Click pe buton pentru a copia.
              </p>
              
              {/* Testimonial Links - Placeholder areas */}
              <TestimonialLinkCard
                title="🎬 Video Testimonial 1"
                description="Adaugă link către primul video testimonial"
                linkPlaceholder="https://youtube.com/watch?v=..."
              />
              
              <TestimonialLinkCard
                title="🎬 Video Testimonial 2"
                description="Adaugă link către al doilea video testimonial"
                linkPlaceholder="https://youtube.com/watch?v=..."
              />
              
              <TestimonialLinkCard
                title="📸 Poveste de succes Instagram"
                description="Link către story sau post Instagram"
                linkPlaceholder="https://instagram.com/p/..."
              />
              
              <TestimonialLinkCard
                title="📄 Pagină rezultate / Studiu de caz"
                description="Link către pagină cu rezultate"
                linkPlaceholder="https://..."
              />
              
              <TestimonialLinkCard
                title="🔗 Link adițional 1"
                description="Orice alt link util"
                linkPlaceholder="https://..."
              />
              
              <TestimonialLinkCard
                title="🔗 Link adițional 2"
                description="Orice alt link util"
                linkPlaceholder="https://..."
              />
            </div>
          )}
        </div>

        {/* Save Button at bottom */}
        <Button 
          onClick={handleSave} 
          disabled={saving} 
          className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 py-3"
        >
          <Save className="h-4 w-4 mr-2" /> 
          {saving ? 'Se salvează...' : 'Salvează toate modificările'}
        </Button>
      </div>
    </Card>
  );
};

export default MagneticTransformation;
